import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import Modal from '../components/Modal';
import LoadingOverlay from '../components/LoadingOverlay';
import AddressAutocomplete from '../components/AddressAutocomplete';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', password: '', password_confirmation: '',
    business_name: '', business_address: '',
    billing_type: 'privato',
    billing_first_name: '', billing_last_name: '', tax_code: '',
    billing_company: '', vat_number: '',
    billing_address: '', billing_city: '', billing_zip: '', billing_province: '',
    sdi_code: '', pec_email: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const upd = (k: string, v: string) => setForm(p => ({ ...p, [k]: v }));
  const isAzienda = form.billing_type === 'azienda';

  const goToStep2 = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password !== form.password_confirmation) {
      setError('Le password non coincidono.');
      return;
    }
    setError('');
    setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isAzienda && !form.sdi_code.trim() && !form.pec_email.trim()) {
      setError('Per le aziende è necessario inserire il Codice SDI o la PEC.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await register(form);
      navigate('/');
    } catch (err: any) {
      const errs = err.response?.data?.errors;
      const msg = err.response?.data?.message || err.response?.data?.error;
      const text = errs ? Object.values(errs).flat().join(' ') : (msg || 'Errore nella registrazione.');
      setError(text);
      if (errs) {
        const step1Fields = ['name','email','password','business_name','business_address'];
        if (Object.keys(errs).some((k: string) => step1Fields.includes(k))) setStep(1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: '28px 20px 40px' }}>
      <div style={{ maxWidth: 390, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 20 }}>

        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 4 }}>
          <img src={logo} alt="Molti Like" style={{ width: 180, height: 'auto', objectFit: 'contain' }} />
        </div>

        <div>
          <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-1)', marginBottom: 4, letterSpacing: '-0.02em' }}>Crea Account</p>
          <p className="sub">{step === 1 ? 'Dati personali e attività' : 'Dati di fatturazione'}</p>
        </div>

        {/* Steps indicator */}
        <div className="steps">
          <div className={`step-dot ${step > 1 ? 'done' : 'active'}`}>
            {step > 1 ? <span className="mi mi-14">check</span> : '1'}
          </div>
          <div className={`step-line${step > 1 ? ' done' : ''}`}></div>
          <div className={`step-dot ${step >= 2 ? 'active' : 'pending'}`}>2</div>
          <div className="step-line"></div>
          <div className="step-dot pending">3</div>
        </div>

        {loading && <LoadingOverlay title="Creazione dell'account..." subtitle="Un momento" icon="person_add" />}
        {error && <Modal isOpen title="Controlla i dati" message={error} type="error" onConfirm={() => setError('')} />}

        {step === 1 ? (
          <form onSubmit={goToStep2} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Dati personali</p>
              <div className="form-group">
                <label className="form-label">Nome completo</label>
                <input className="form-input" type="text" placeholder="Mario Rossi" value={form.name} onChange={e => upd('name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input className="form-input" type="email" placeholder="mario@esempio.com" value={form.email} onChange={e => upd('email', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Telefono <span style={{ fontSize: 11, color: 'var(--text-3)' }}>(opzionale)</span></label>
                <input className="form-input" type="tel" placeholder="+39 333 1234567" value={form.phone} onChange={e => upd('phone', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" placeholder="Minimo 8 caratteri" value={form.password} onChange={e => upd('password', e.target.value)} required minLength={8} />
              </div>
              <div className="form-group">
                <label className="form-label">Conferma password</label>
                <input className="form-input" type="password" placeholder="Ripeti la password" value={form.password_confirmation} onChange={e => upd('password_confirmation', e.target.value)} required />
              </div>
            </div>

            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>La tua attività</p>
              <div className="form-group">
                <label className="form-label">Nome attività</label>
                <input className="form-input" type="text" placeholder="Pizzeria Mario" value={form.business_name} onChange={e => upd('business_name', e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Indirizzo attività</label>
                <input className="form-input" type="text" placeholder="Via Roma 1, 09100 Cagliari" value={form.business_address} onChange={e => upd('business_address', e.target.value)} required />
              </div>
            </div>

            <div className="alert info">
              <span className="mi mi-18">location_on</span>
              <span>L'indirizzo serve per mostrare i tuoi contenuti alle persone vicino alla tua attività.</span>
            </div>

            <button type="submit" className="btn btn-primary">Continua</button>

            <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-2)' }}>
              Hai già un account?{' '}
              <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}>Accedi</Link>
            </p>
          </form>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* Tipo intestatario */}
            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Tipo intestatario</p>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{key:'privato',label:'Privato',icon:'person'},{key:'azienda',label:'Azienda',icon:'business'}].map(t => (
                  <button key={t.key} type="button"
                    onClick={() => upd('billing_type', t.key)}
                    className={`btn ${form.billing_type === t.key ? 'btn-primary' : 'btn-outline'}`}
                    style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 11 }}>
                    <span className="mi mi-18">{t.icon}</span> {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Dati fiscali */}
            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{isAzienda ? 'Dati azienda' : 'Dati personali'}</p>
              {isAzienda ? (
                <>
                  <div className="form-group">
                    <label className="form-label">Ragione Sociale *</label>
                    <input className="form-input" placeholder="Pizzeria Da Mario S.r.l." value={form.billing_company} onChange={e => upd('billing_company', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Partita IVA *</label>
                    <input className="form-input" placeholder="IT12345678901" value={form.vat_number} onChange={e => upd('vat_number', e.target.value)} maxLength={13} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Codice Fiscale <span style={{ fontSize: 11, color: 'var(--text-3)' }}>(se diverso da P.IVA)</span></label>
                    <input className="form-input" placeholder="Opzionale" value={form.tax_code} onChange={e => upd('tax_code', e.target.value.toUpperCase())} maxLength={16} />
                  </div>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Nome *</label>
                      <input className="form-input" placeholder="Mario" value={form.billing_first_name} onChange={e => upd('billing_first_name', e.target.value)} required />
                    </div>
                    <div className="form-group" style={{ flex: 1 }}>
                      <label className="form-label">Cognome *</label>
                      <input className="form-input" placeholder="Rossi" value={form.billing_last_name} onChange={e => upd('billing_last_name', e.target.value)} required />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Codice Fiscale *</label>
                    <input className="form-input" placeholder="RSSMRA80A01H501Z" value={form.tax_code} onChange={e => upd('tax_code', e.target.value.toUpperCase())} maxLength={16} required />
                  </div>
                </>
              )}
            </div>

            {/* Indirizzo fatturazione */}
            <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
              <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Indirizzo di fatturazione</p>
              <AddressAutocomplete
                label="Indirizzo *"
                value={form.billing_address}
                onChange={v => upd('billing_address', v)}
                placeholder="Via Roma 1"
                onSelect={parsed => {
                  if (parsed.address)  upd('billing_address', parsed.address);
                  if (parsed.city)     upd('billing_city', parsed.city);
                  if (parsed.zip)      upd('billing_zip', parsed.zip);
                  if (parsed.province) upd('billing_province', parsed.province);
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <div className="form-group" style={{ width: 90, flexShrink: 0 }}>
                  <label className="form-label">CAP *</label>
                  <input className="form-input" placeholder="09100" value={form.billing_zip} onChange={e => upd('billing_zip', e.target.value)} maxLength={5} required />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Città *</label>
                  <input className="form-input" placeholder="Cagliari" value={form.billing_city} onChange={e => upd('billing_city', e.target.value)} required />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Provincia * <span style={{ fontSize: 11, color: 'var(--text-3)' }}>(sigla, es. CA)</span></label>
                <input className="form-input" placeholder="CA" value={form.billing_province} onChange={e => upd('billing_province', e.target.value.toUpperCase().slice(0,2))} maxLength={2} required style={{ width: 80 }} />
              </div>
            </div>

            {/* SDI/PEC solo azienda */}
            {isAzienda && (
              <div style={{ background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Fatturazione elettronica{' '}
                  <span style={{ color: 'var(--error)', fontSize: 10, textTransform: 'none' }}>(obbligatorio almeno uno)</span>
                </p>
                <div className="form-group">
                  <label className="form-label">Codice SDI (7 caratteri)</label>
                  <input className="form-input" placeholder="0000000" value={form.sdi_code} onChange={e => upd('sdi_code', e.target.value.toUpperCase().slice(0,7))} maxLength={7} />
                </div>
                <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--text-3)' }}>oppure</div>
                <div className="form-group">
                  <label className="form-label">PEC</label>
                  <input className="form-input" type="email" placeholder="fatture@pec.esempio.it" value={form.pec_email} onChange={e => upd('pec_email', e.target.value)} />
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setStep(1); setError(''); }}>
                Indietro
              </button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={loading}>
                Crea account
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
